<template>
  <div class="py-3">
    <h4 class="font-serif mb-4">
      {{ $t("tradingCenter.tabs.issuance_reporting.title") }}
    </h4>

    <p v-if="!company.id" class="text-body-secondary">
      {{ $t("issuance_reporting.company_required") }}
    </p>
    <div v-else-if="loadingContext" class="py-5 text-center" role="status">
      {{ $t("common.loading") }}
    </div>
    <template v-else-if="cnmcContext">
      <button
        v-if="!capabilities"
        class="btn btn-outline-primary mb-3"
        @click="loadContext"
      >
        {{ $t("common.retry") }}
      </button>
      <SpanishIssuanceWorkflow
        v-if="showCnmcWorkflow"
        :key="company.id"
        :initial-context="cnmcContext"
        :company
        :account="loaded ? accountData : null"
        :canImport="capabilities?.can_import ?? false"
        :isAdmin
      />
      <template v-if="!hasEnabledConnection">
        <p
          v-if="effectiveCountryCode === 'ES' && !showCnmcWorkflow"
          class="alert alert-subtle-info"
        >
          {{
            $t(
              cnmcContext.connections.length
                ? "cnmc.preparation_disabled"
                : "cnmc.connection_required"
            )
          }}
        </p>
        <!-- Country Selection (Admin Only) -->
        <div v-if="isAdmin" class="alert alert-subtle-warning mb-4">
          <CountrySelector v-model="selectedCountry" :disabled="isProcessing" />
        </div>

        <!-- Action Bar (hidden for LT companies - admin operations handled in /admin/eso/power-bi) -->
        <div
          v-if="!isLithuanianCompany"
          class="d-flex justify-content-between align-items-center mb-4"
        >
          <div class="d-flex gap-2">
            <button
              class="btn btn-primary"
              @click="showFileUploadModal"
              :disabled="isProcessing || !capabilities?.can_import"
            >
              <font-awesome-icon icon="cloud-upload" class="me-1" />
              {{ $t("issuance_reporting.upload_file") }}
            </button>
          </div>

          <div class="d-flex gap-2">
            <button
              v-if="store.selectedCount && store.selectedCount > 0"
              class="btn btn-outline-secondary btn-sm"
              @click="store.clearSelection"
              :disabled="isProcessing"
            >
              <font-awesome-icon icon="times" class="me-1" />
              {{ $t("common.clear") }}
            </button>
            <button
              v-if="canExport"
              class="btn btn-outline-success"
              @click="exportData"
              :disabled="isProcessing"
            >
              <font-awesome-icon icon="download" class="me-1" />
              {{ $t("issuance_reporting.export") }}
            </button>
          </div>
        </div>
        <p
          v-if="
            !isLithuanianCompany && capabilities && !capabilities.can_import
          "
          class="text-body-secondary"
        >
          {{ $t("issuance_reporting.import_not_trusted") }}
        </p>

        <!-- Issuance Production Data Table -->
        <IssuanceProductionDataTable
          ref="issuanceProductionTable"
          :company="company"
          :is-admin="isAdmin"
        />

        <!-- File Upload Modal (not used for LT companies) -->
        <FileUploadModal
          v-if="!isLithuanianCompany"
          ref="fileUploadModal"
          :company="company"
          :registryDevices="registryDevices"
          :country-code="effectiveCountryCode"
          :canImport="capabilities?.can_import ?? false"
        />
      </template>
    </template>
    <button v-else class="btn btn-outline-primary" @click="loadContext">
      {{ $t("common.retry") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import SpanishIssuanceWorkflow from "./es/SpanishIssuanceWorkflow.vue";
import { cnmcApi } from "./es/cnmcApi";
import type { CnmcContext } from "./es/cnmcTypes";
import {
  issuanceReportingApi,
  type IssuanceCapabilities,
} from "./issuanceReportingApi";
import { useAccount } from "@/composables/account/useAccount";
import { isAdminAccount } from "@/composables/useAdminCheck";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import CountrySelector from "@/dashboard/production_strategy/issuance-reporting/CountrySelector.vue";
import FileUploadModal from "@/dashboard/production_strategy/issuance-reporting/FileUploadModal.vue";
import IssuanceProductionDataTable from "@/dashboard/production_strategy/issuance-reporting/IssuanceProductionDataTable.vue";
import { useIssuanceProductionDataStore } from "@/dashboard/production_strategy/issuance-reporting/useIssuanceProductionDataStore";
import { useEventBus } from "@vueuse/core";
import { registryConnectionsChanged } from "@/registryEvents";
import type { Company, RegistryDevice } from "@/types";
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company: Company;
  registryDevices?: RegistryDevice[];
}>();

const registryDevices = computed(() => props.registryDevices ?? []);

const { t } = useI18n();
const store = useIssuanceProductionDataStore();
const { accountData, loaded } = useAccount();
const isAdmin = computed(() => loaded.value && isAdminAccount(accountData));
const fileUploadModal =
  useTemplateRef<InstanceType<typeof FileUploadModal>>("fileUploadModal");
const issuanceProductionTable =
  ref<InstanceType<typeof IssuanceProductionDataTable>>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// Local state for country selection
const selectedCountry = ref("");

const cnmcContext = ref<CnmcContext>();
const capabilities = ref<IssuanceCapabilities>();
const loadingContext = ref(false);
let contextRevision = 0;

const isLithuanianCompany = computed(() => props.company.country === "LT");
const hasEnabledConnection = computed(() =>
  Boolean(
    cnmcContext.value?.preparation_enabled &&
    cnmcContext.value.connections.some(
      (connection) => connection.active && connection.preparation_enabled
    )
  )
);
const showCnmcWorkflow = computed(
  () =>
    hasEnabledConnection.value ||
    cnmcContext.value?.connections.some((connection) => connection.has_history)
);
const canExport = computed(
  () =>
    capabilities.value?.export_countries.includes(
      effectiveCountryCode.value ?? ""
    ) ?? false
);

const isProcessing = computed(() => store.loading);

// Get the effective country code - admin can override, regular users use company default
const effectiveCountryCode = computed(() => {
  // For admin users, use selectedCountry if set, otherwise fall back to company country
  // For regular users, always use company country
  if (isAdmin.value) {
    return selectedCountry.value || props.company.country;
  }
  return props.company.country;
});

// Methods
const loadContext = async () => {
  const companyId = props.company.id;
  const revision = ++contextRevision;
  cnmcContext.value = undefined;
  capabilities.value = undefined;
  loadingContext.value = Boolean(companyId);
  if (!companyId) return;
  const [contextResponse, capabilitiesResponse] = await Promise.allSettled([
    cnmcApi.context(companyId),
    issuanceReportingApi.capabilities(companyId),
  ]);
  if (revision !== contextRevision) return;
  if (contextResponse.status === "fulfilled")
    cnmcContext.value = contextResponse.value.data;
  else globalErrorHandler(contextResponse.reason);
  if (capabilitiesResponse.status === "fulfilled")
    capabilities.value = capabilitiesResponse.value.data;
  else globalErrorHandler(capabilitiesResponse.reason);
  loadingContext.value = false;
};

useEventBus(registryConnectionsChanged).on((companyId) => {
  if (companyId === props.company.id) void loadContext();
});

watch(
  () => props.company.id,
  () => {
    selectedCountry.value = props.company.country ?? "";
    void loadContext();
  },
  { immediate: true }
);
onUnmounted(() => contextRevision++);

const showFileUploadModal = () => {
  fileUploadModal.value?.show();
};

const exportData = async () => {
  if (!canExport.value) return;
  try {
    // Get current filters from the issuance production table
    const currentFilters =
      issuanceProductionTable.value?.getCurrentFilters() || {};

    // Add country code to filters
    const filtersWithCountry = {
      ...currentFilters,
      country_code: effectiveCountryCode.value,
    };

    if (props.company.id) {
      await store.exportData(props.company.id, filtersWithCountry);
    }

    globalSuccessHandler(
      t("common.success"),
      t("issuance_reporting.export_success")
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};
</script>

<style scoped lang="scss">
// Component now focuses on layout and orchestration
// Individual components handle their own styling
</style>
